import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import TableCard from "../components/ui-components/TableCard";
import FlexTable from "../components/ui-components/FlexTable";
import EmptyState from "../components/ui-components/EmptyState";
import Badge from "../components/ui-components/Badge";
import { listTenants } from "./platformApi";

// Badge takes two independent things and both have to be supplied here.
// `status` picks the tone, and Badge only understands its own vocabulary --
// none of the four lifecycle values is in its STATUS_TONE map, so all four
// would come out neutral. `label` is rendered verbatim (Badge capitalises
// only when label is null), so it must arrive already capitalised.
const STATUS_TONE = {
	provisioning: "pending",
	active: "success",
	suspended: "warning",
	archived: "cancelled",
};

const statusLabel = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

// Resource columns show an explicit marker rather than an empty cell: a
// tenant with no bucket of its own silently inherits the process-wide one,
// which is exactly the thing an operator needs to see at a glance.
const resourceCell = (value) =>
	value ? (
		<span className="font-mono text-[12px]">{value}</span>
	) : (
		<span className="text-[12px] italic text-text-3">not set</span>
	);

export default function TenantListPage() {
	const navigate = useNavigate();

	// The app-wide default is staleTime: 15 minutes with no refetch on focus
	// (src/index.js), which suits cost figures that are stable within a
	// period. It is wrong here: this list is the operator's own registry and
	// the operator is the one mutating it, so returning to this page after
	// creating, provisioning or suspending a tenant showed a cached list
	// without the change -- the tenant looked as though it had not been
	// created, while creating it again failed with "id is already in use".
	//
	// refetchOnMount rather than invalidation alone: TenantDetailPage drives
	// its own writes with useState/useEffect and holds no query client, so
	// nothing there can invalidate this key.
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ["platform", "tenants"],
		queryFn: listTenants,
		staleTime: 0,
		refetchOnMount: "always",
	});

	const columns = useMemo(
		() => [
			{ Header: "Tenant", accessor: "id", width: 160 },
			{ Header: "Display name", accessor: "displayName", width: 200 },
			{
				Header: "Status",
				accessor: "status",
				width: 120,
				Cell: ({ value }) => (
					<Badge status={STATUS_TONE[value] || "neutral"} label={statusLabel(value)} size="sm" />
				),
			},
			{ Header: "Dataset", accessor: "bqDataset", width: 180, Cell: ({ value }) => resourceCell(value) },
			{ Header: "Bucket", accessor: "gcsBucket", width: 180, Cell: ({ value }) => resourceCell(value) },
			{
				Header: "Service account",
				accessor: "bqServiceAccount",
				width: 300,
				Cell: ({ value }) => resourceCell(value),
			},
		],
		[]
	);

	const tenants = data || [];

	return (
		<TableCard
			title="Tenants"
			subtitle="Every tenant record on this platform, and the physical resources bound to it."
			isLoading={isLoading}
			error={error}
			errorMessage="Could not load tenants"
			onRetry={refetch}
			// TableCard takes a descriptor here, not a node. Passing a node
			// renders its own empty button with no label and no handler --
			// which is what this was doing, so the button did nothing.
			primaryAction={{ label: "Create tenant", onClick: () => navigate("/tenants/new") }}
		>
			{tenants.length === 0 ? (
				<EmptyState title="No tenants" message="Create the first tenant record to begin." />
			) : (
				<FlexTable
					columns={columns}
					data={tenants}
					getRowId={(row) => row.id}
					onRowClick={(rowId) => navigate(`/tenants/${rowId}`)}
				/>
			)}
		</TableCard>
	);
}
